/**
 * @typedef {Object} RecipeLineItemCreateDto
 * @property {number} childComponentId
 * @property {number} quantity
 * @property {number} [scrapPercentage]
 * @property {number} [sequenceOrder]
 * @property {boolean} [isAlternative]
 * @property {number} [alternativeGroup]
 */

/**
 * @typedef {Object} RecipeLineItemUpdateDto
 * @property {number} [childComponentId]
 * @property {number} [quantity]
 * @property {number} [scrapPercentage]
 * @property {number} [sequenceOrder]
 * @property {boolean} [isAlternative]
 * @property {number} [alternativeGroup]
 * @property {boolean} [isActive]
 */

/**
 * @typedef {Object} RecipeLineItemResponseDto
 * @property {number} recipeId
 * @property {number} recipeVersionId
 * @property {number} parentComponentId
 * @property {string} parentComponentCode
 * @property {string} parentComponentName
 * @property {number} childComponentId
 * @property {string} childComponentCode
 * @property {string} childComponentName
 * @property {number} quantity
 * @property {number} scrapPercentage
 * @property {number} [sequenceOrder]
 * @property {boolean} isAlternative
 * @property {number} [alternativeGroup]
 * @property {boolean} isActive
 * @property {string} createdDate
 * @property {string} [createdBy]
 * @property {string} [modifiedDate]
 * @property {string} [modifiedBy]
 * @property {number} quantityWithScrap
 */

/**
 * @typedef {Object} RecipeVersionCreateDto
 * @property {number} componentId
 * @property {string} [versionName]
 * @property {string} effectiveDate
 * @property {string} [expiryDate]
 * @property {number} [yieldPercentage]
 * @property {boolean} [isCurrentVersion]
 * @property {string} [notes]
 * @property {RecipeLineItemCreateDto[]} [lineItems]
 */

/**
 * @typedef {Object} RecipeVersionUpdateDto
 * @property {string} [versionName]
 * @property {string} [effectiveDate]
 * @property {string} [expiryDate]
 * @property {number} [yieldPercentage]
 * @property {boolean} [isCurrentVersion]
 * @property {string} [notes]
 * @property {boolean} [isActive]
 */

/**
 * @typedef {Object} RecipeVersionResponseDto
 * @property {number} recipeVersionId
 * @property {number} componentId
 * @property {string} componentCode
 * @property {string} componentName
 * @property {number} versionNumber
 * @property {string} [versionName]
 * @property {string} effectiveDate
 * @property {string} [expiryDate]
 * @property {number} yieldPercentage
 * @property {boolean} isCurrentVersion
 * @property {string} [notes]
 * @property {boolean} isActive
 * @property {string} createdDate
 * @property {string} [createdBy]
 * @property {string} [modifiedDate]
 * @property {string} [modifiedBy]
 * @property {RecipeLineItemResponseDto[]} [lineItems]
 * @property {number} lineItemCount
 */

/**
 * @typedef {Object} RecipeVersionListResponse
 * @property {RecipeVersionResponseDto[]} versions
 * @property {number} totalCount
 * @property {number} pageNumber
 * @property {number} pageSize
 * @property {number} totalPages
 */

/**
 * @typedef {Object} RecipeVersionLookupDto
 * @property {number} recipeVersionId
 * @property {number} componentId
 * @property {string} componentCode
 * @property {string} componentName
 * @property {number} versionNumber
 * @property {string} [versionName]
 * @property {boolean} isCurrentVersion
 * @property {boolean} isActive
 */

/**
 * @typedef {Object} WhereUsedItemDto
 * @property {number} parentComponentId
 * @property {string} parentComponentCode
 * @property {string} parentComponentName
 * @property {number} recipeVersionId
 * @property {number} versionNumber
 * @property {number} quantity
 * @property {boolean} isCurrentVersion
 */

/**
 * @typedef {Object} WhereUsedDto
 * @property {number} childComponentId
 * @property {string} childComponentCode
 * @property {string} childComponentName
 * @property {WhereUsedItemDto[]} usedIn
 */

const BASE_PATH = 'v1/Recipe';

export default function (instance) {
  return {
    // ==================== Recipe Versions ====================

    /**
     * Get all recipe versions with pagination
     * @param {Object} [params]
     * @param {boolean} [params.includeInactive=false]
     * @param {number} [params.pageNumber=1]
     * @param {number} [params.pageSize=50]
     * @returns {Promise<{data: RecipeVersionListResponse}>}
     */
    getRecipeVersions(params = {}) {
      return instance.get(`${BASE_PATH}/versions`, { params });
    },

    /**
     * Get recipe version by ID with line items
     * @param {number} id
     * @param {Object} [params]
     * @param {boolean} [params.includeLineItems=true]
     * @returns {Promise<{data: RecipeVersionResponseDto}>}
     */
    getRecipeVersion(id, params = {}) {
      return instance.get(`${BASE_PATH}/versions/${id}`, { params });
    },

    /**
     * Create new recipe version with optional line items
     * @param {RecipeVersionCreateDto} payload
     * @returns {Promise<{data: RecipeVersionResponseDto}>}
     */
    createRecipeVersion(payload) {
      return instance.post(`${BASE_PATH}/versions`, payload);
    },

    /**
     * Update recipe version
     * @param {number} id
     * @param {RecipeVersionUpdateDto} payload
     * @returns {Promise<{data: RecipeVersionResponseDto}>}
     */
    updateRecipeVersion(id, payload) {
      return instance.put(`${BASE_PATH}/versions/${id}`, payload);
    },

    /**
     * Delete recipe version (soft delete)
     * @param {number} id
     * @returns {Promise<void>}
     */
    deleteRecipeVersion(id) {
      return instance.delete(`${BASE_PATH}/versions/${id}`);
    },

    /**
     * Get all recipe versions for a component
     * @param {number} componentId
     * @param {Object} [params]
     * @param {boolean} [params.includeInactive=false]
     * @returns {Promise<{data: RecipeVersionResponseDto[]}>}
     */
    getComponentRecipeVersions(componentId, params = {}) {
      return instance.get(`${BASE_PATH}/component/${componentId}/versions`, { params });
    },

    /**
     * Get current active recipe version for a component
     * @param {number} componentId
     * @returns {Promise<{data: RecipeVersionResponseDto}>}
     */
    getCurrentRecipeVersion(componentId) {
      return instance.get(`${BASE_PATH}/component/${componentId}/current`);
    },

    /**
     * Get recipe version lookups for dropdowns
     * @param {Object} [params]
     * @param {number} [params.componentId]
     * @returns {Promise<{data: RecipeVersionLookupDto[]}>}
     */
    getRecipeVersionLookups(params = {}) {
      return instance.get(`${BASE_PATH}/versions/lookups`, { params });
    },

    /**
     * Set a recipe version as the current active version
     * @param {number} id
     * @returns {Promise<void>}
     */
    setCurrentVersion(id) {
      return instance.post(`${BASE_PATH}/versions/${id}/set-current`);
    },

    /**
     * Copy a recipe version to create a new version
     * @param {number} id
     * @param {string} [newVersionName]
     * @returns {Promise<{data: RecipeVersionResponseDto}>}
     */
    copyRecipeVersion(id, newVersionName) {
      const params = newVersionName ? { newVersionName } : {};
      return instance.post(`${BASE_PATH}/versions/${id}/copy`, null, { params });
    },

    // ==================== Recipe Line Items ====================

    /**
     * Get all line items for a recipe version
     * @param {number} versionId
     * @param {Object} [params]
     * @param {boolean} [params.includeInactive=false]
     * @returns {Promise<{data: RecipeLineItemResponseDto[]}>}
     */
    getLineItems(versionId, params = {}) {
      return instance.get(`${BASE_PATH}/versions/${versionId}/line-items`, { params });
    },

    /**
     * Get recipe line item by ID
     * @param {number} id
     * @returns {Promise<{data: RecipeLineItemResponseDto}>}
     */
    getLineItem(id) {
      return instance.get(`${BASE_PATH}/line-items/${id}`);
    },

    /**
     * Add a line item to a recipe version
     * @param {number} versionId
     * @param {RecipeLineItemCreateDto} payload
     * @returns {Promise<{data: RecipeLineItemResponseDto}>}
     */
    createLineItem(versionId, payload) {
      return instance.post(`${BASE_PATH}/versions/${versionId}/line-items`, payload);
    },

    /**
     * Update a recipe line item
     * @param {number} id
     * @param {RecipeLineItemUpdateDto} payload
     * @returns {Promise<{data: RecipeLineItemResponseDto}>}
     */
    updateLineItem(id, payload) {
      return instance.put(`${BASE_PATH}/line-items/${id}`, payload);
    },

    /**
     * Delete a recipe line item (soft delete)
     * @param {number} id
     * @returns {Promise<void>}
     */
    deleteLineItem(id) {
      return instance.delete(`${BASE_PATH}/line-items/${id}`);
    },

    // ==================== Where Used ====================

    /**
     * Get where a component is used as an ingredient
     * @param {number} componentId
     * @param {Object} [params]
     * @param {boolean} [params.currentVersionOnly=true]
     * @returns {Promise<{data: WhereUsedDto}>}
     */
    getWhereUsed(componentId, params = {}) {
      return instance.get(`${BASE_PATH}/where-used/${componentId}`, { params });
    }
  };
}
